/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  KnownMembership,
  MatrixError,
  RoomEvent,
  type MatrixClient,
  type Room,
} from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";

import { Config } from "../config/Config";
import { fallbackICEServerAllowed, initClient } from "../utils/matrix";
import { type GuestCredentials } from "./callLinksApi";

/**
 * Signs in with the temporary account the guest service issued. Everything is
 * kept in memory: the account lives for one call, and the browser may already
 * be signed in to Element Call with someone's real account, whose stores must
 * not be touched.
 */
export async function createGuestClient(
  credentials: GuestCredentials,
): Promise<MatrixClient> {
  return initClient(
    {
      baseUrl: credentials.homeserver,
      accessToken: credentials.accessToken,
      userId: credentials.userId,
      deviceId: credentials.deviceId,
      fallbackICEServerAllowed,
      livekitServiceURL: Config.get().livekit?.livekit_service_url,
    },
    false,
    true,
  );
}

/** Ends the guest's session; the service deletes the account itself later. */
export function disposeGuestClient(client: MatrixClient): void {
  client.stopClient();
  client
    .logout(true)
    .catch((e) => logger.warn("Could not sign the guest out", e));
}

const JOIN_ATTEMPTS = 3;
const JOIN_RETRY_MS = 3000;

async function joinAfterInvite(
  client: MatrixClient,
  roomId: string,
): Promise<void> {
  for (let attempt = 1; ; attempt++) {
    try {
      await client.joinRoom(roomId);
      return;
    } catch (e) {
      // Just after the invite the server may not have caught up yet
      if (
        e instanceof MatrixError &&
        e.errcode === "M_FORBIDDEN" &&
        attempt < JOIN_ATTEMPTS
      ) {
        await new Promise((resolve) => setTimeout(resolve, JOIN_RETRY_MS));
        continue;
      }
      throw e;
    }
  }
}

export type Admission = "admitted" | "rejected";

/**
 * Waits for the call's creator (or a room admin) to answer the guest's knock:
 * an invite lets the guest in — and we join — a kick (leave) or ban keeps
 * them out. Resolves at once if the guest is in already.
 */
export async function awaitAdmission(
  client: MatrixClient,
  roomId: string,
  signal: AbortSignal,
): Promise<Admission> {
  const decide = async (
    membership: string | undefined,
  ): Promise<Admission | null> => {
    switch (membership) {
      case KnownMembership.Join:
        return "admitted";
      case KnownMembership.Invite:
        await joinAfterInvite(client, roomId);
        return "admitted";
      case KnownMembership.Leave:
      case KnownMembership.Ban:
        return "rejected";
      default:
        // Knocking, or the room has not reached us yet
        return null;
    }
  };

  return new Promise<Admission>((resolve, reject) => {
    let settled = false;
    const settle = (membership: string | undefined): void => {
      if (settled || membership === undefined) return;
      if (membership === KnownMembership.Knock) return;
      settled = true;
      cleanUp();
      decide(membership).then(
        (answer) => resolve(answer ?? "rejected"),
        reject,
      );
    };
    const onMembership = (room: Room, membership: string): void => {
      if (room.roomId === roomId) settle(membership);
    };
    const onAbort = (): void => {
      if (settled) return;
      settled = true;
      cleanUp();
      reject(signal.reason ?? new Error("Stopped waiting for admission"));
    };
    const cleanUp = (): void => {
      client.off(RoomEvent.MyMembership, onMembership);
      signal.removeEventListener("abort", onAbort);
    };
    client.on(RoomEvent.MyMembership, onMembership);
    signal.addEventListener("abort", onAbort);
    if (signal.aborted) onAbort();
    // The answer may have come before we started listening
    else settle(client.getRoom(roomId)?.getMyMembership());
  });
}
